import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Loader from "../common/Loader.jsx";

// Context & Permissions
import { useGlobal } from "../../GlobalContext";
import { canEdit, canViewSensitiveFields } from "../common/permissionUtils";
import Back from "../../assets/svg/back.svg";
import amountIcon from "../../assets/svg/amount.svg";
import ITIcon from "../../assets/svg/it.svg";
import { getItemViewApi } from "./item-api";
import "./item-create.css";
import "./item-view.css";

const ItemView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const itemId = location.state?.id;

  const { rolePermissionList } = useGlobal();
  const hasEditPermission = canEdit(rolePermissionList, "Item");
  const hasSensitiveFieldAccess = canViewSensitiveFields(rolePermissionList, "Item");

  const [itemData, setItemData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!itemId) return;
    fetchItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId]);

  const fetchItem = async () => {
    setLoading(true);
    try {
      const response = await getItemViewApi(itemId);
      if (response?.data?.success) {
        setItemData(response.data.item);
      }
    } catch (error) {
      console.error("Error fetching item:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <Loader height="80vh" />
      </div>
    );
  }

  const isActive = !!itemData?.status;
  const margin = (Number(itemData?.price) || 0) - (Number(itemData?.purchase_price) || 0);

  return (
    <div className="item-view-page">
      <div className="iv-header">
        <div className="iv-header-left">
          <div className="iv-back-btn" onClick={() => navigate(-1)}>
            <img src={Back} alt="Back" />
          </div>
          <div className="iv-title-block">
            <div className="iv-item-name">{itemData?.item_name || "Item"}</div>
            <div className="iv-item-sub">
              {itemData?.item_code ? `SKU: ${itemData.item_code}` : "No SKU"}
              {itemData?.hsn_no ? ` · HSN: ${itemData.hsn_no}` : ""}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <span
            className="iv-status-pill"
            style={{
              backgroundColor: isActive ? "#d9ffe9" : "#FDF0F0",
              color: isActive ? "#2d9d78" : "#d61916",
            }}
          >
            {isActive ? "Active" : "Inactive"}
          </span>
          {hasEditPermission && (
            <button className="btn-save" onClick={() => navigate("/item/create", { state: { id: itemId } })}>
              Edit
            </button>
          )}
        </div>
      </div>

      <div className="iv-body">
        {/* Sidebar */}
        <div className="iv-sidebar-card">
          <div className="iv-image-box">
            {itemData?.upload ? (
              <img src={itemData.upload} alt={itemData?.item_name || "Item"} />
            ) : (
              <span className="iv-image-placeholder">No image</span>
            )}
          </div>

          <div className="iv-sidebar-divider" />

          <div>
            <div className="iv-fact-item">
              <div className="iv-fact-icon-box">
                <img src={ITIcon} alt="Brand" />
              </div>
              <div>
                <div className="iv-fact-label">Brand</div>
                <div className="iv-fact-value">{itemData?.brand || "--"}</div>
              </div>
            </div>
            <div className="iv-fact-item">
              <div className="iv-fact-icon-box">
                <img src={ITIcon} alt="Item Type" />
              </div>
              <div>
                <div className="iv-fact-label">Item Type</div>
                <div className="iv-fact-value">{itemData?.item_type || "--"}</div>
              </div>
            </div>
            <div className="iv-fact-item">
              <div className="iv-fact-icon-box">
                <img src={amountIcon} alt="Price" />
              </div>
              <div>
                <div className="iv-fact-label">Price</div>
                <div className="iv-fact-value">₹{itemData?.price ?? "0.00"}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="iv-main">
          <div className="iv-stats-row">
            <div className="iv-stat-item">
              <span className="iv-stat-label">Price</span>
              <span className="iv-stat-value">₹{itemData?.price ?? "0.00"}</span>
            </div>
            {hasSensitiveFieldAccess && (
              <div className="iv-stat-item">
                <span className="iv-stat-label">Purchase Price</span>
                <span className="iv-stat-value">₹{itemData?.purchase_price ?? "0.00"}</span>
              </div>
            )}
            <div className="iv-stat-item">
              <span className="iv-stat-label">MRP</span>
              <span className="iv-stat-value">₹{itemData?.mrp ?? "0.00"}</span>
            </div>
            {hasSensitiveFieldAccess && (
              <div className="iv-stat-item">
                <span className="iv-stat-label">Margin</span>
                <span className="iv-stat-value" style={{ color: margin >= 0 ? "#1aa56a" : "#e53935" }}>
                  ₹{margin.toFixed(2)}
                </span>
              </div>
            )}
            <div className="iv-stat-item">
              <span className="iv-stat-label">Current Stock</span>
              <span className="iv-stat-value">{itemData?.available_stock ?? "0"}</span>
            </div>
          </div>

          <div className="iv-card">
            <div className="iv-card-title">Classification</div>
            <div className="iv-detail-grid">
              <div>
                <div className="iv-detail-label">Brand</div>
                <div className="iv-detail-value">{itemData?.brand || "--"}</div>
              </div>
              <div>
                <div className="iv-detail-label">Item Type</div>
                <div className="iv-detail-value">{itemData?.item_type || "--"}</div>
              </div>
              <div>
                <div className="iv-detail-label">Material Type</div>
                <div className="iv-detail-value">{itemData?.material_type || "--"}</div>
              </div>
              <div>
                <div className="iv-detail-label">UOM</div>
                <div className="iv-detail-value">{itemData?.uom || "--"}</div>
              </div>
              <div>
                <div className="iv-detail-label">HSN Code</div>
                <div className="iv-detail-value">{itemData?.hsn_no || "--"}</div>
              </div>
              <div>
                <div className="iv-detail-label">SKU</div>
                <div className="iv-detail-value">{itemData?.item_code || "--"}</div>
              </div>
            </div>
          </div>

          <div className="iv-card">
            <div className="iv-card-title">Pricing</div>
            <div className="iv-detail-grid">
              <div>
                <div className="iv-detail-label">Discount Percentage</div>
                <div className="iv-detail-value">{itemData?.discount_percentage ?? "0"}%</div>
              </div>
              <div>
                <div className="iv-detail-label">Tax Percentage</div>
                <div className="iv-detail-value">{itemData?.tax_percentage ?? "0"}%</div>
              </div>
            </div>
          </div>

          <div className="iv-card">
            <div className="iv-card-title">Stock</div>
            <div className="iv-detail-grid">
              <div>
                <div className="iv-detail-label">Initial Stock</div>
                <div className="iv-detail-value">{itemData?.initial_stock ?? "0"}</div>
              </div>
              {/* <div>
                <div className="iv-detail-label">Current Stock</div>
                <div className="iv-detail-value">{itemData?.available_stock ?? "0"}</div>
              </div> */}
              <div>
                <div className="iv-detail-label">Stock Avail</div>
                <div className="iv-detail-value">{itemData?.stock_avail ?? "0"}</div>
              </div>
              <div>
                <div className="iv-detail-label">Minimum Stock</div>
                <div className="iv-detail-value">{itemData?.minimum_stock ?? "0"}</div>
              </div>
            </div>
          </div>

          {itemData?.description && (
            <div className="iv-card">
              <div className="iv-card-title">Description</div>
              <div className="iv-description">{itemData.description}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ItemView;
